import { z } from "zod";

import { richTextToPlainText } from "~/components/ui/rich-text-editor";

import {
  ENCRYPTED_RICH_TEXT_MAX_LENGTH,
  encryptedField,
  encryptionIv,
} from "./encrypted-field";
import { KeyGrantsSchema } from "./key-grants";

export const CATEGORY_NAME_MAX_LENGTH = 150;
export const CATEGORY_SHORT_DESCRIPTION_MAX_LENGTH = 10_240;

const richTextDescriptionValidation = z
  .string()
  .optional()
  .refine(
    (value) => {
      if (!value) return true;
      const plainText = richTextToPlainText(value);
      return (
        plainText.replace(/\n/g, "").length <=
        CATEGORY_SHORT_DESCRIPTION_MAX_LENGTH
      );
    },
    {
      message: `Description must be ${CATEGORY_SHORT_DESCRIPTION_MAX_LENGTH} characters or less`,
    },
  );

export const GetCategoryOfLifeUrlSchema = z.object({
  categoryId: z.string().uuid(),
  filename: z.string(),
  contentType: z.string(),
});

const EncryptedCategoryFields = {
  encryptedName: encryptedField(),
  encryptedShortDescription: encryptedField(ENCRYPTED_RICH_TEXT_MAX_LENGTH),
  iv: encryptionIv,
  keyGrants: KeyGrantsSchema,
};

/** The browser makes the id, so the row is created once, with its keys. */
export const CreateCategoryOfLifeSchema = z.object({
  categoryId: z.string().uuid(),
  imageUrl: z.string().nullable(),
  ...EncryptedCategoryFields,
});

export const UpdateCategoryOfLifeWithImageSchema = z.object({
  id: z.string().uuid(),
  imageUrl: z.string().nullable(),
  /** The new plain name, for the public copy on this category's shared goals. */
  shareCategoryName: z.string().max(CATEGORY_NAME_MAX_LENGTH).optional(),
  ...EncryptedCategoryFields,
});

export const DeleteCategoryOfLifeSchema = z.object({
  id: z.string().uuid(),
});

export const GetSharedCategoriesSchema = z.object({
  userId: z.string().uuid(),
});

export const GetCategoryByIdSchema = z.object({
  id: z.string().uuid(),
});

export type EncryptedCategoryData = Pick<
  z.infer<typeof CreateCategoryOfLifeSchema>,
  "encryptedName" | "encryptedShortDescription" | "iv" | "keyGrants"
>;

export const CategoryFormSchema = z.object({
  name: z
    .string()
    .min(1, "Title is required")
    .max(
      CATEGORY_NAME_MAX_LENGTH,
      `Title must be ${CATEGORY_NAME_MAX_LENGTH} characters or less`,
    ),
  shortDescription: richTextDescriptionValidation,
  file: z.instanceof(File).optional().nullable(),
  removeExistingImage: z.boolean().optional(),
});

export type CategoryFormData = z.infer<typeof CategoryFormSchema>;
